import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { YOUTUBE_API_SEARCH_URL } from "../utils/constants"
import { cacheSearchResults } from '../utils/videoSlice'
import { Link } from 'react-router-dom'
import { useSearchParams } from "react-router-dom"
import { formatTimeAgo } from '../utils/helper'

const SearchCard = ({ info }) => {
  const { snippet } = info
  const { thumbnails, title, channelTitle, publishedAt } = snippet

  return (
    <div className="flex flex-col sm:flex-row p-2 m-2 shadow-lg rounded-lg hover:bg-gray-100">
      <div className="w-full sm:w-96 h-48 sm:h-52 flex-shrink-0">
        <img
          className="rounded-lg w-full h-full object-cover"
          src={thumbnails.high.url}
          alt={title}
        />
      </div>

      <div className="flex flex-col mt-3 sm:mt-0 sm:ml-4 flex-1 min-w-0">
        <h3 className="font-semibold text-base sm:text-xl line-clamp-2">
          {title}
        </h3>

        <div className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600">
          {formatTimeAgo(publishedAt)}
        </div>

        <div className="mt-1 sm:mt-2 flex items-center">
          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-200 flex-shrink-0"></div>
          <span className="ml-2 text-xs sm:text-sm font-medium text-gray-700 truncate">
            {channelTitle}
          </span>
        </div>

        <div className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600 line-clamp-2">
          {snippet.description}
        </div>
      </div>
    </div>
  )
}

const SearchPage = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchParams] = useSearchParams()
  const query = searchParams.get("q")

  const dispatch = useDispatch()
  const cachedResults = useSelector(store => store.video.searchResults[query])

  useEffect(() => {
    if (!query) {
      setLoading(false)
      return
    }

    if (cachedResults) {
      setLoading(false)
      return
    }

    const getVideos = async () => {
      try {
        setLoading(true)
        setError(null)
        const p = await fetch(YOUTUBE_API_SEARCH_URL + encodeURIComponent(query))
        const json = await p.json()
        dispatch(cacheSearchResults({ query, results: json.items || [] }))
      }
      catch (e) {
        console.error(e)
        setError("Failed to load search results. Please try again later.")
      }
      finally {
        setLoading(false)
      }
    }
    getVideos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, dispatch])

  const videos = cachedResults || []

  if (loading) return (
    <div className="flex justify-center items-center py-20">
      <div className="animate-spin h-10 w-10 border-4 border-gray-300 border-t-red-500 rounded-full"></div>
    </div>
  )

  if (error) return (
    <div className="flex justify-center items-center py-20 text-red-500">
      <p>{error}</p>
    </div>
  )

  if (!query) return (
    <div className="flex justify-center items-center py-20 text-gray-500">
      <p>Enter a search term to find videos.</p>
    </div>
  )

  if (videos.length === 0) return (
    <div className="flex justify-center items-center py-20 text-gray-500">
      <p>No results found for &ldquo;{query}&rdquo;.</p>
    </div>
  )

  return (
    <div className="max-w-4xl mx-auto px-2 sm:px-0">
      {videos.map(video => (
        <Link key={video.id.videoId} to={"/watch?v=" + video.id.videoId}>
          <SearchCard info={video} />
        </Link>
      ))}
    </div>
  )
}

export default SearchPage